// What the Plan screens show (redesign slice 5b; wireframes 2, 3 and 14):
// the week heading, one row per day with dinner leading, each day's dishes
// with their scaling, and the day's time and nutrition status. Pure
// functions, tested in tests/plan-view.test.mjs.
import { DINING_OUT, MEAL_SLOTS, MISSING_RECIPE_LABEL, PLANNABLE_DISH_TYPES, RECIPE_TYPE_LABELS } from "./constants";
import { normalizeDishes, dayCookMinutes } from "./planUtils";
import { formatMinutes, shiftWeek } from "./utils";
import { drawsOn, mealDate, mealKey, mealLabel, plannedServings, scaleFactorFor, weekServings } from "./planScaling";
import type { Day, DishType, MealSlot, PlansByWeek, RecipeById, WeekPlan } from "./types";

// ------------------------------------------------------------ the week ---

const shortDate = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

// "Sep 28 to Oct 4"
export function weekRange(weekStart: string): string {
  const start = new Date(`${weekStart}T00:00:00`);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  return `${shortDate(start)} to ${shortDate(end)}`;
}

// "This week", "Next week", "Last week", or "Week of" further away.
export function weekLabel(weekStart: string, thisWeek: string): string {
  if (weekStart === thisWeek) return "This week";
  if (weekStart === shiftWeek(thisWeek, 1)) return "Next week";
  if (weekStart === shiftWeek(thisWeek, -1)) return "Last week";
  return "Week of";
}

const filled = (week: WeekPlan | undefined, day: Day, slot: MealSlot) =>
  Object.values(normalizeDishes(week?.days?.[day]?.[slot]) as Record<string, string>).some(Boolean);

// "7 dinners, 3 lunches" -- for "Start from last week".
export function weekSummary(week: WeekPlan | undefined, days: readonly Day[]): string {
  const count = (slot: MealSlot) => days.filter((d) => filled(week, d, slot)).length;
  const parts = (
    [
      ["dinner", "dinners"],
      ["lunch", "lunches"],
      ["breakfast", "breakfasts"],
    ] as const
  )
    .map(([slot, plural]) => {
      const n = count(slot);
      return n === 0 ? "" : `${n} ${n === 1 ? slot : plural}`;
    })
    .filter(Boolean);
  return parts.join(", ") || "nothing planned";
}

// The recipes a share covers (slice 8b): one day's dishes, or the whole
// week's, plus its snacks, preserves and medicinal lists when included.
// Dining out and empty rows aren't recipes.
export function planRecipeIds(week: WeekPlan | undefined, day: Day | null, includeLists = false): Set<string> {
  const ids = new Set<string>();
  const days = day ? [day] : (Object.keys(week?.days || {}) as Day[]);
  for (const d of days) for (const slot of MEAL_SLOTS) for (const id of Object.values(normalizeDishes(week?.days?.[d]?.[slot]) as Record<string, string>)) if (id && id !== DINING_OUT) ids.add(id);
  if (!day && includeLists) for (const list of ["snacks", "preserves", "medicinal"] as const) (week?.[list] || []).forEach((e) => ids.add(e.recipeId));
  return ids;
}

// Recipes planned in the two weeks before this one ("repeat" badges).
export function recentRecipeIds(plansByWeek: PlansByWeek, weekStart: string): Set<string> {
  const ids = new Set<string>();
  for (const n of [1, 2]) {
    const wd = plansByWeek[shiftWeek(weekStart, -n)];
    if (!wd) continue;
    for (const slots of Object.values(wd.days || {})) for (const dishes of Object.values(slots || {})) for (const id of Object.values(dishes || {})) if (id) ids.add(id);
    for (const list of ["snacks", "preserves", "medicinal"] as const) (wd[list] || []).forEach((e) => ids.add(e.recipeId));
  }
  return ids;
}

// ------------------------------------------------------------- a dish ---

export interface DishLine {
  dishType: DishType;
  label: string; // "Main", "Side", or "Leftovers"
  recipeId: string | null; // null for dining out or a recipe the viewer can't see
  title: string;
  isLeftover: boolean;
  isDiningOut: boolean;
  isRepeat: boolean;
  meta: string; // "45 min · scaled from 4 to 8 servings", "From Thursday's dinner · not on the grocery list"
  reason: string; // "4 at this meal + 4 as leftovers for Sunday's lunch", when scaled
  scaledTo: number | null; // the planned servings, when the recipe is scaled up to them
  cooked: boolean; // marked cooked (0058); only ever true for a cooked dish
}

export function dishLines(plansByWeek: PlansByWeek, weekStart: string, day: Day, slot: MealSlot, recipeById: RecipeById, now: Date, recent: Set<string> = new Set()): DishLine[] {
  const week = plansByWeek[weekStart];
  const dishes = normalizeDishes(week?.days?.[day]?.[slot]) as Partial<Record<DishType, string>>;
  const lines: DishLine[] = [];
  for (const dishType of PLANNABLE_DISH_TYPES) {
    const id = dishes[dishType];
    if (!id) continue;
    const key = mealKey(day, slot, dishType);
    const typeLabel = RECIPE_TYPE_LABELS[dishType];
    if (id === DINING_OUT) {
      lines.push({ dishType, label: typeLabel, recipeId: null, title: "Dining out", isLeftover: false, isDiningOut: true, isRepeat: false, meta: "", reason: "", scaledTo: null, cooked: false });
      continue;
    }
    const recipe = recipeById(id);
    const isLeftover = !!week?.leftovers?.[key];
    // Leftovers are the same meal eaten again, never a "repeat".
    const base = { dishType, recipeId: recipe ? id : null, title: recipe ? recipe.title : MISSING_RECIPE_LABEL, isDiningOut: false, isRepeat: !isLeftover && recent.has(id) };
    if (isLeftover) {
      const source = week?.leftoverSources?.[key];
      const from = source ? `From ${mealLabel(source, now)}` : "Leftovers from earlier";
      lines.push({ ...base, label: "Leftovers", isLeftover: true, meta: `${from} · not on the grocery list`, reason: "", scaledTo: null, cooked: false });
      continue;
    }
    const ref = { weekStart, day, mealSlot: slot, dishType };
    const planned = plannedServings(plansByWeek, ref);
    const scaled = scaleFactorFor(recipe, planned) > 1;
    const minutes = recipe ? (recipe.activeTime || 0) + (recipe.cookTime || 0) : 0;
    const meta = [minutes ? formatMinutes(minutes) : "", scaled && recipe ? `scaled from ${recipe.servings} to ${planned} servings` : ""].filter(Boolean).join(" · ");
    const draws = drawsOn(plansByWeek, ref).filter((d) => d.scalesSource);
    const reason =
      scaled && draws.length > 0
        ? [`${weekServings(plansByWeek, weekStart)} at this meal`, ...draws.map((d) => `${weekServings(plansByWeek, d.ref.weekStart)} as leftovers for ${mealLabel(d.ref, now)}`)].join(" + ")
        : "";
    lines.push({ ...base, label: typeLabel, isLeftover: false, meta, reason, scaledTo: scaled ? planned : null, cooked: !!week?.cooked?.[key] });
  }
  return lines;
}

// ---------------------------------------------------------- a day row ---

export interface DayRow {
  day: Day;
  date: Date;
  isToday: boolean;
  dinner: DishLine | null; // the dinner's first dish
  markers: string[]; // "B", "L" for a planned breakfast or lunch
  status: string; // "Repeat from last week", "40 min over time budget", ""
}

export function dayRow(plansByWeek: PlansByWeek, weekStart: string, day: Day, recipeById: RecipeById, now: Date, recent: Set<string>): DayRow {
  const week = plansByWeek[weekStart];
  const date = mealDate(weekStart, day);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dinner = dishLines(plansByWeek, weekStart, day, "dinner", recipeById, now, recent)[0] || null;
  const markers = [filled(week, day, "breakfast") ? "B" : "", filled(week, day, "lunch") ? "L" : ""].filter(Boolean);
  const budget = budgetStatus(week, day, recipeById);
  const status = budget?.over ? `${budget.text} time budget` : dinner?.isRepeat ? "Repeat from the last two weeks" : "";
  return { day, date, isToday: date.getTime() === today.getTime(), dinner, markers, status };
}

// ------------------------------------------------------ day status chips ---

// "20 min free" / "40 min over" against the day's time budget, or null when
// the day has no budget.
export function budgetStatus(week: WeekPlan | undefined, day: Day, recipeById: RecipeById): { text: string; over: boolean } | null {
  const budget = week?.dayBudgets?.[day];
  if (budget == null) return null;
  const used = dayCookMinutes(week, day, recipeById);
  return used > budget ? { text: `${formatMinutes(used - budget)} over`, over: true } : { text: `${formatMinutes(budget - used)} free`, over: false };
}

export interface Diner {
  name: string;
  targetsHidden?: boolean;
  dailyCalorieTarget: number;
  dailyProteinTarget: number;
  dailyCarbsTarget: number;
  dailyFatTarget: number;
}

// "Nutrition: 2 of 2 on target", with one line per person for the
// explanation. Only people whose numbers this viewer may see (their own and
// unlinked diners) count -- someone else's targets are private.
export function nutritionStatus(week: WeekPlan | undefined, day: Day, recipeById: RecipeById, diners: Diner[]): { text: string; over: boolean; lines: string[] } | null {
  const people = diners.filter((m) => !m.targetsHidden && (m.dailyCalorieTarget > 0 || m.dailyProteinTarget > 0 || m.dailyCarbsTarget > 0 || m.dailyFatTarget > 0));
  if (people.length === 0) return null;
  const totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
  for (const slot of MEAL_SLOTS) {
    for (const id of Object.values(normalizeDishes(week?.days?.[day]?.[slot]) as Record<string, string>)) {
      const r = id ? recipeById(id) : undefined;
      if (!r) continue;
      totals.calories += r.caloriesPerServing || 0;
      totals.protein += r.proteinPerServing || 0;
      totals.carbs += r.carbsPerServing || 0;
      totals.fat += r.fatPerServing || 0;
    }
  }
  const details = people.map((m) => {
    const checks = (
      [
        ["cal", totals.calories, m.dailyCalorieTarget],
        ["g protein", totals.protein, m.dailyProteinTarget],
        ["g carbs", totals.carbs, m.dailyCarbsTarget],
        ["g fat", totals.fat, m.dailyFatTarget],
      ] as const
    ).filter(([, , target]) => target > 0);
    const over = checks.some(([, actual, target]) => actual > target);
    return { over, line: `${m.name || "Someone"}: ${checks.map(([label, actual, target]) => `${Math.round(actual)} of ${target} ${label}`).join(", ")}${over ? " (over)" : ""}` };
  });
  const onTarget = details.filter((d) => !d.over).length;
  return { text: `Nutrition: ${onTarget} of ${people.length} on target`, over: onTarget < people.length, lines: details.map((d) => d.line) };
}
